import { useEffect, useState } from "react"
import { getActiveSyncEngine } from "./reset"
import type { OnDemandWindow } from "./on-demand"

export interface UseOnDemandWindowResult {
  /** True while the below-floor slice is being fetched. */
  loading: boolean
  /**
   * True when the slice couldn't be fetched (offline / network error). The
   * recap uses this to show "connect to load older orders" instead of an
   * empty list. False once a fetch succeeds (rows then flow in via Dexie).
   */
  offlineMiss: boolean
}

/**
 * Below-floor ON-DEMAND window fetch (STK-1323).
 *
 * When `enabled` (the requested recap date range is older than the kept
 * window), this fires a one-shot fetch of that slice into Dexie via the active
 * SyncEngine. The ROWS themselves are read by the caller's own `useLiveQuery`
 * — which re-runs reactively when the on-demand upserts land — so this hook
 * only owns the `loading` / `offlineMiss` status, not the data.
 *
 * Re-fetches whenever the axis/from/to change. Disabled → idle (no fetch), so
 * in-window recaps (served fully from Dexie) never hit the network.
 */
export function useOnDemandWindow(
  params: OnDemandWindow & { enabled: boolean }
): UseOnDemandWindowResult {
  const { enabled, ...window } = params
  const [state, setState] = useState<UseOnDemandWindowResult>({
    loading: false,
    offlineMiss: false,
  })
  // Stable primitive dep — re-fires the fetch only when the window actually
  // changes (the object identity churns every render).
  const windowKey = JSON.stringify(window)

  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, offlineMiss: false })
      return
    }
    const engine = getActiveSyncEngine()
    if (!engine) {
      // No engine yet (pre-boot) — treat as a transient miss; the effect
      // re-runs when deps change and the engine is available.
      setState({ loading: false, offlineMiss: true })
      return
    }

    let cancelled = false
    setState({ loading: true, offlineMiss: false })
    engine
      .fetchOnDemandWindow(window)
      .then((res) => {
        if (!cancelled) {
          setState({ loading: false, offlineMiss: res.offlineMiss })
        }
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, offlineMiss: true })
      })
    return () => {
      cancelled = true
    }
    // `window` is reconstructed from `windowKey`; depend on the stable key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, windowKey])

  return state
}
